import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  ViewChild,
  computed,
  inject,
  input,
  model,
  output,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";

interface EmojiCategory {
  icon: string;
  label: string;
  emojis: string[];
}

@Component({
  selector: "app-composer",
  standalone: true,
  imports: [FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./composer.component.html",
  styleUrl: "./composer.component.scss",
})
export class ComposerComponent {
  readonly draft = model<string>("");
  readonly replyTo = input<{ id: string; text: string } | null>(null);
  readonly hasDraft = input<boolean>(false);

  readonly send = output<void>();
  readonly skip = output<void>();
  readonly cancelReply = output<void>();
  readonly sendImage = output<string>();
  readonly typing = output<void>();

  private lastTypingEmit = 0;

  @ViewChild("imageInput") imageInput?: ElementRef<HTMLInputElement>;
  @ViewChild("messageInput") messageInput?: ElementRef<HTMLTextAreaElement>;

  private readonly zone = inject(NgZone);

  readonly pendingImage = signal<string | null>(null);
  readonly hasSendable = computed(
    () => this.hasDraft() || this.pendingImage() !== null,
  );

  // Called by parent when user clicks outside the composer area
  closePickerFromOutside() {
    this.emojiPickerOpen.set(false);
  }

  focusInput() {
    this.messageInput?.nativeElement.focus();
  }

  openImagePicker(event: MouseEvent) {
    event.stopPropagation();
    this.imageInput?.nativeElement.click();
  }

  onImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    input.value = "";
    if (!file.type.startsWith("image/")) return;

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const MAX = 900;
      let { width, height } = img;
      if (width > height) {
        if (width > MAX) {
          height = Math.round((height * MAX) / width);
          width = MAX;
        }
      } else {
        if (height > MAX) {
          width = Math.round((width * MAX) / height);
          height = MAX;
        }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d")!.drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
      URL.revokeObjectURL(objectUrl);
      // FileReader/Image callbacks run outside Angular's zone — force CD
      this.zone.run(() => this.pendingImage.set(dataUrl));
    };
    img.src = objectUrl;
  }

  clearPendingImage() {
    this.pendingImage.set(null);
  }

  onSend() {
    const img = this.pendingImage();
    if (img) {
      this.sendImage.emit(img);
      this.pendingImage.set(null);
    } else {
      this.send.emit();
    }
  }

  onTypingInput(value: string) {
    if (!value.trim()) return;
    const now = Date.now();
    if (now - this.lastTypingEmit < 1500) return;
    this.lastTypingEmit = now;
    this.typing.emit();
  }

  readonly emojiPickerOpen = signal(false);
  readonly activeCategoryIndex = signal(0);

  readonly emojiCategories: EmojiCategory[] = [
    {
      icon: "😊",
      label: "Smileys",
      emojis: [
        "😀",
        "😁",
        "😂",
        "🤣",
        "😃",
        "😄",
        "😅",
        "😆",
        "😊",
        "😍",
        "🥰",
        "🤩",
        "😘",
        "😗",
        "🙂",
        "😏",
        "😒",
        "😞",
        "😔",
        "😟",
        "😕",
        "🙁",
        "😣",
        "😖",
        "😫",
        "😩",
        "🥺",
        "😢",
        "😭",
        "😤",
        "😠",
        "😡",
        "🤬",
        "🤯",
        "😳",
        "🥵",
        "🥶",
        "😱",
        "😨",
        "😰",
        "😥",
        "😓",
        "🤔",
        "🤭",
        "🤫",
        "🤥",
        "😶",
        "😐",
        "😑",
        "😬",
        "🙄",
        "😯",
        "😦",
        "😧",
        "😮",
        "😲",
        "🥴",
        "😴",
        "🤤",
        "😪",
        "😵",
        "🤐",
        "🥱",
        "🤢",
        "🤧",
        "🤮",
        "😷",
        "🤒",
        "🤕",
        "🤑",
        "🤠",
        "😎",
        "🤓",
        "🧐",
        "🥳",
        "🥸",
      ],
    },
    {
      icon: "👍",
      label: "Gestures",
      emojis: [
        "👍",
        "👎",
        "👋",
        "🤚",
        "🖐",
        "✋",
        "🤙",
        "👊",
        "✊",
        "🤛",
        "🤜",
        "🤞",
        "🤟",
        "🤘",
        "👌",
        "🤌",
        "✌️",
        "🖖",
        "🫶",
        "👏",
        "🙌",
        "🤲",
        "🙏",
        "💪",
        "🦾",
        "🤝",
        "👐",
        "🫱",
        "🫲",
        "☝️",
        "👆",
        "👇",
        "👉",
        "👈",
        "🤏",
        "🖕",
        "🫵",
        "🫳",
        "🫴",
      ],
    },
    {
      icon: "❤️",
      label: "Hearts",
      emojis: [
        "❤️",
        "🧡",
        "💛",
        "💚",
        "💙",
        "💜",
        "🖤",
        "🤍",
        "🤎",
        "💔",
        "❤️‍🔥",
        "❤️‍🩹",
        "💕",
        "💞",
        "💓",
        "💗",
        "💖",
        "💘",
        "💝",
        "💟",
        "🩷",
        "🩵",
        "🩶",
        "💋",
        "😻",
        "💌",
        "🌹",
        "🌷",
        "💒",
        "🫀",
      ],
    },
    {
      icon: "🎉",
      label: "Fun",
      emojis: [
        "🎉",
        "🎊",
        "🎈",
        "🎁",
        "🏆",
        "🥇",
        "🥈",
        "🥉",
        "🎮",
        "🎯",
        "🎲",
        "🃏",
        "🎭",
        "🎨",
        "🎵",
        "🎶",
        "🎸",
        "🎹",
        "🎺",
        "🎻",
        "🥁",
        "🎷",
        "🌟",
        "⭐",
        "💫",
        "✨",
        "🌈",
        "🦄",
        "🔥",
        "💥",
        "🍀",
        "🌺",
        "🌸",
        "🌻",
        "🌼",
        "🌝",
        "🌛",
        "🌜",
        "🌚",
        "🌕",
        "🌖",
        "🌗",
        "🌘",
        "🌑",
        "🌒",
        "🌓",
        "🌔",
      ],
    },
    {
      icon: "🍕",
      label: "Food",
      emojis: [
        "🍕",
        "🍔",
        "🍟",
        "🌮",
        "🌯",
        "🥙",
        "🧆",
        "🥚",
        "🍳",
        "🥘",
        "🍲",
        "🥣",
        "🥗",
        "🍿",
        "🧂",
        "🧀",
        "🍱",
        "🍘",
        "🍙",
        "🍚",
        "🍛",
        "🍜",
        "🍝",
        "🍠",
        "🦪",
        "🍣",
        "🍤",
        "🍙",
        "🍦",
        "🍧",
        "🍨",
        "🍩",
        "🍪",
        "🎂",
        "🍰",
        "🧁",
        "🥧",
        "🍫",
        "🍬",
        "🍭",
        "🍮",
        "🧃",
        "🥤",
        "🧋",
        "☕",
        "🍵",
        "🧉",
        "🍺",
        "🍻",
        "🥂",
        "🍾",
      ],
    },
  ];

  toggleEmojiPicker(event: MouseEvent) {
    event.stopPropagation();
    this.emojiPickerOpen.update((v) => !v);
  }

  closeEmojiPicker() {
    this.emojiPickerOpen.set(false);
  }

  selectCategory(index: number) {
    this.activeCategoryIndex.set(index);
  }

  insertEmoji(emoji: string) {
    this.draft.update((v) => v + emoji);
  }
}
